import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  ReactNode,
} from 'react';
import axios from 'axios';
import { SessionExpiryModal } from '../components/SessionExpiryModal';

const AUTH_API_URL = import.meta.env.VITE_BFF_API_URL || 'http://localhost:8086';
const AUTH_APP_URL = import.meta.env.VITE_AUTH_APP_URL || 'http://localhost:3000';

export interface User {
  id: string;
  email: string;
  username: string;
  first_name: string;
  last_name: string;
  role: string;
  status: string;
  pin_enabled: boolean;
}

export interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  checkSession: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
  loginUrl?: string;
}

function cookieAttrs(): string {
  const cookieDomain = import.meta.env.VITE_COOKIE_DOMAIN || '';
  const domain = cookieDomain ? `; domain=${cookieDomain}` : '';
  const isSecure = typeof window !== 'undefined' && window.location.protocol === 'https:';
  const sameSite = isSecure ? '; SameSite=None; Secure' : '';
  return `${domain}${sameSite}`;
}

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  for (const cookie of document.cookie.split(';')) {
    const separator = cookie.indexOf('=');
    if (separator === -1) continue;
    const key = cookie.slice(0, separator).trim();
    if (key === name) {
      return decodeURIComponent(cookie.slice(separator + 1));
    }
  }
  return null;
}

function writeCookie(name: string, value: string, maxAge: number) {
  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${maxAge}${cookieAttrs()}`;
}

function clearCookie(name: string) {
  document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT${cookieAttrs()}`;
}

function getSessionToken(): string | null {
  return readCookie('session_token');
}

function expiryFromAccessToken(token: string): number | null {
  const payload = token.split('.')[1];
  if (!payload) return null;
  try {
    const padded = payload.replace(/-/g, '+').replace(/_/g, '/');
    const json = JSON.parse(atob(padded));
    return typeof json.exp === 'number' ? json.exp * 1000 : null;
  } catch {
    return null;
  }
}

const WARNING_THRESHOLD = 120;

function AuthProvider({ children, loginUrl }: AuthProviderProps) {
  const loginRedirect = loginUrl || `${AUTH_APP_URL}/login`;
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [refreshToken, setRefreshToken] = useState<string | null>(() => {
    if (typeof window !== 'undefined') {
      return sessionStorage.getItem('refresh_token');
    }
    return null;
  });
  const [showExpiryModal, setShowExpiryModal] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  const [extending, setExtending] = useState(false);
  const sessionExpiresAtRef = useRef<number>(0);
  const checkIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const clearSessionData = useCallback(() => {
    clearCookie('session_token');
    clearCookie('refresh_token');
    clearCookie('session_expires_at');
    setUser(null);
    setIsAuthenticated(false);
    setRefreshToken(null);
    sessionStorage.removeItem('refresh_token');
    sessionStorage.removeItem('session_expires_at');
    setShowExpiryModal(false);
    sessionExpiresAtRef.current = 0;
  }, []);

  const doLogout = useCallback(async () => {
    try {
      if (refreshToken) {
        await axios.post(
          `${AUTH_API_URL}/api/v1/auth/logout`,
          { refresh_token: refreshToken },
          { withCredentials: true },
        );
      }
    } catch {
      // Ignore logout errors
    } finally {
      clearSessionData();
      window.location.href = loginRedirect;
    }
  }, [refreshToken, clearSessionData, loginRedirect]);

  const startExpiryCheck = useCallback(() => {
    if (checkIntervalRef.current) clearInterval(checkIntervalRef.current);

    checkIntervalRef.current = setInterval(() => {
      const expiresAt = sessionExpiresAtRef.current;
      if (!expiresAt || expiresAt <= 0) return;

      const now = Date.now();
      const remaining = Math.max(0, Math.floor((expiresAt - now) / 1000));

      if (remaining <= 0) {
        if (checkIntervalRef.current) clearInterval(checkIntervalRef.current);
        doLogout();
        return;
      }

      setRemainingSeconds(remaining);

      if (remaining <= WARNING_THRESHOLD) {
        setShowExpiryModal(true);
      }
    }, 1000);
  }, [doLogout]);

  const extendSession = useCallback(async () => {
    const tokenToRefresh =
      refreshToken || sessionStorage.getItem('refresh_token') || readCookie('refresh_token');
    if (!tokenToRefresh) return;

    if (checkIntervalRef.current) clearInterval(checkIntervalRef.current);

    setExtending(true);
    try {
      const res = await axios.post(
        `${AUTH_API_URL}/api/v1/auth/refresh`,
        { refresh_token: tokenToRefresh },
        { withCredentials: true },
      );
      const data = res.data;
      if (data?.access_token && data?.expires_in) {
        writeCookie('session_token', data.access_token, data.expires_in);

        const newExpiresAt = Date.now() + data.expires_in * 1000;
        sessionExpiresAtRef.current = newExpiresAt;
        sessionStorage.setItem('session_expires_at', String(newExpiresAt));
        writeCookie('session_expires_at', String(newExpiresAt), data.expires_in);

        if (data.refresh_token) {
          setRefreshToken(data.refresh_token);
          sessionStorage.setItem('refresh_token', data.refresh_token);
          writeCookie('refresh_token', data.refresh_token, data.expires_in);
        }

        setShowExpiryModal(false);
        startExpiryCheck();
      } else {
        startExpiryCheck();
      }
    } catch (error) {
      const status = axios.isAxiosError(error) ? error.response?.status : undefined;
      if (status === 401) {
        await doLogout();
        return;
      }
      startExpiryCheck();
    } finally {
      setExtending(false);
    }
  }, [refreshToken, doLogout, startExpiryCheck]);

  const checkSession = useCallback(async () => {
    const token = getSessionToken();
    if (!token) {
      setIsAuthenticated(false);
      setUser(null);
      setIsLoading(false);
      return;
    }

    try {
      const res = await axios.get<User>(`${AUTH_API_URL}/api/v1/auth/me`, {
        withCredentials: true,
      });
      setUser(res.data);
      setIsAuthenticated(true);

      const stored =
        sessionStorage.getItem('session_expires_at') || readCookie('session_expires_at');
      const parsed = stored ? parseInt(stored, 10) : NaN;
      const expiry =
        !isNaN(parsed) && parsed > Date.now() ? parsed : expiryFromAccessToken(token) || 0;
      if (expiry > Date.now()) {
        sessionExpiresAtRef.current = expiry;
        sessionStorage.setItem('session_expires_at', String(expiry));
        startExpiryCheck();
      }

      const refresh = sessionStorage.getItem('refresh_token') || readCookie('refresh_token');
      if (refresh) {
        setRefreshToken(refresh);
        sessionStorage.setItem('refresh_token', refresh);
      }
    } catch {
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  }, [startExpiryCheck]);

  useEffect(() => {
    checkSession();
    return () => {
      if (checkIntervalRef.current) clearInterval(checkIntervalRef.current);
    };
  }, [checkSession]);

  const login = async (email: string, password: string) => {
    setIsLoading(true);
    try {
      const res = await axios.post(
        `${AUTH_API_URL}/api/v1/auth/login`,
        { email, password },
        { withCredentials: true },
      );
      if (res.data?.user && res.data?.access_token) {
        writeCookie('session_token', res.data.access_token, res.data.expires_in);
        setUser(res.data.user);
        setIsAuthenticated(true);

        const newExpiresAt = Date.now() + res.data.expires_in * 1000;
        sessionExpiresAtRef.current = newExpiresAt;
        sessionStorage.setItem('session_expires_at', String(newExpiresAt));
        writeCookie('session_expires_at', String(newExpiresAt), res.data.expires_in);
        startExpiryCheck();

        if (res.data?.refresh_token) {
          setRefreshToken(res.data.refresh_token);
          sessionStorage.setItem('refresh_token', res.data.refresh_token);
          writeCookie('refresh_token', res.data.refresh_token, res.data.expires_in);
        }
      } else {
        throw new Error('Login failed');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    await doLogout();
  };

  const handleModalLogout = () => {
    doLogout();
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        isLoading,
        login,
        logout,
        checkSession,
      }}
    >
      {children}
      <SessionExpiryModal
        open={showExpiryModal}
        remainingSeconds={remainingSeconds}
        onExtend={extendSession}
        onLogout={handleModalLogout}
        extending={extending}
      />
    </AuthContext.Provider>
  );
}

function useAuth(): AuthContextType {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}

export { AuthProvider, useAuth };
